import { Observable, BehaviorSubject, of as observableOf, of } from "rxjs";
import { mergeMap, catchError, share, filter } from "rxjs/operators";
import { AuthenticationService } from "../AuthenticationService/authentication.service";
import { TimeSheetDelegate } from "./timesheet.delegate";
import { IBaseResponse, ITimeCard, ITimeSheet, ITimeSheetResponse } from "./timesheet.interface";

/**
 * Employee-scoped timesheet facade (formerly TimeSheetService in
 * employeeServicelib). Construction is handled by TimeSheetServiceFactory
 * and access by ServiceLocator.getTimeSheetService().
 */
export class TimeSheetService
{
    private timeSheetsData: ITimeSheet[] = null;
    private timeSheetsSubject: BehaviorSubject<ITimeSheet[]> = new BehaviorSubject<ITimeSheet[]>(null);
    public timeSheets: Observable<ITimeSheet[]> = this.timeSheetsSubject.pipe(filter((data) => !!data));

    private lastTimeSheetSubmitDate: Date = null;
    private lastTimesheetSubmitDateSubject: BehaviorSubject<Date> = new BehaviorSubject<Date>(null);
    public lastSubmitTimeSheetDate: Observable<Date> = this.lastTimesheetSubmitDateSubject.pipe(filter((data) => !!data));

    private selectedTimeSheetId: string = '0';
    private selectedProjectCodeId: string = '0';
    private lastTimesheetSubmitDate: Date;

    constructor(
        private timeSheetDelegate: TimeSheetDelegate,
        private authenticationService: AuthenticationService
    ) { }

    public get TimeSheetId(): string
    {
        return this.selectedTimeSheetId;
    }

    public setSelectedTimeSheetId(timeSheetId: string): void
    {
        this.selectedTimeSheetId = timeSheetId;
    }

    public get ProjectCodeId(): string
    {
        return this.selectedProjectCodeId;
    }

    public setSelectedProjectCodeId(projectCodeId: string): void
    {
        this.selectedProjectCodeId = projectCodeId;
    }

    public get LastSubmissionTimesheetDate(): Date
    {
        return this.lastTimesheetSubmitDate;
    }

    public getSelectedTimeSheetData(): ITimeSheet
    {
        return this.timeSheetsData &&
            this.timeSheetsData.find((timeSheet: ITimeSheet) => timeSheet.timeSheetId === this.selectedTimeSheetId);
    }

    public createNewEmployeeTimeCard(empTimecard: ITimeCard): Observable<any>
    {
        const response = this.timeSheetDelegate.createNewEmployeeTimeCard({
            ...empTimecard,
            ...this.authenticationService.getBaseRequest
        });
        response.subscribe(onTimeSheetSuccess.bind(this), onTimeSheetFault.bind(this));

        return response.pipe(
            mergeMap((r: any) => observableOf(r)),
            catchError((error: any) => observableOf(error)),
            share());

        function onTimeSheetSuccess(clientResponse: IBaseResponse)
        {
            if (clientResponse && clientResponse.success)
            {
                this.lastTimeSheetSubmitDate = clientResponse.lastTimeSheetSubmitDate as Date;
                this.lastTimesheetSubmitDateSubject.next(this.lastTimeSheetSubmitDate);
            }
        }

        function onTimeSheetFault(_error: any) { /* noop */ }
    }

    public updateEmployeeTimeCard(empTimecard: ITimeCard): Observable<any>
    {
        const response = this.timeSheetDelegate.updateNewEmployeeTimeCard({
            ...empTimecard,
            ...this.authenticationService.getBaseRequest
        });

        return response.pipe(
            mergeMap((r: any) => observableOf(r)),
            catchError((error: any) => observableOf(error)),
            share());
    }

    public getTimeSheetsByProjectId(projectCodeId: string): Observable<IBaseResponse>
    {
        const response = this.timeSheetDelegate.getAllTimeSheetsByProjectId(projectCodeId);
        return this.processTimeSheets(response);
    }

    private processTimeSheets(timeSheets: Observable<ITimeSheetResponse>): Observable<IBaseResponse>
    {
        timeSheets.subscribe(onTimeSheetSuccess.bind(this), onTimeSheetFault.bind(this));

        return timeSheets.pipe(
            mergeMap((response: ITimeSheetResponse) => observableOf({
                success: response.success,
                errorString: response.errorString
            } as IBaseResponse)),
            catchError((response: ITimeSheetResponse) => of({
                success: response && response.success,
                errorString: response && response.errorString
            } as IBaseResponse)),
            share());

        function onTimeSheetSuccess(clientResponse: ITimeSheetResponse)
        {
            if (clientResponse && clientResponse.success)
            {
                this.timeSheetsData = clientResponse.timeSheets;
                this.lastTimeSheetSubmitDate = clientResponse.lastTimeSheetSubmitDate;
                this.lastTimesheetSubmitDateSubject.next(this.lastTimeSheetSubmitDate);
                this.timeSheetsSubject.next(this.timeSheetsData);
            }
        }

        function onTimeSheetFault(_error: any) { /* noop */ }
    }
}
